import { Injectable, Inject } from '@angular/core';
import { Router, ActivatedRoute, ResolveEnd, ActivatedRouteSnapshot } from '@angular/router';
import { AppInsights } from 'applicationinsights-js';

import { Subscription, Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DOCUMENT } from '@angular/common';
import { LoggerService } from '../../log4ts/logger.service';
import {Store} from '@ngrx/store';
import {IAppState} from '../../../store/state/app.state';
import {PageErrorPage} from '../../../store/actions/page.actions';
import {GetBalanceSuccess} from '../../../store/actions/balance.actions';
import {IBalance} from '../../../models/balance.interface';

/**
 * Service to register appInsights
 */
@Injectable({
  providedIn: 'root',
})
export class MonitoringService {
  /**
   * Extra small media size
   */
  public cID = '';

  /**
   * Configuration key
   */
  private config: Microsoft.ApplicationInsights.IConfig = {
    instrumentationKey: environment.appInsightsConfig.instrumentationKey,
  };

  /**
   * Service constructor download and setup AppInsights
   */
  constructor(@Inject(DOCUMENT) private doc, private console: LoggerService, public _store: Store<IAppState>) {
    if (environment.production && !AppInsights.config) {
      AppInsights.downloadAndSetup(this.config);
    }
  }

  /**
   * Log page view
   * @param name page name
   * @param url url page
   * @param properties properties
   * @param measurements measurments
   * @param duration duration
   */
  logPageView(name: string, url?: string, properties?: any, measurements?: any, duration?: number) {
    AppInsights.trackPageView(
      name,
      url,
      this.AddGlobalProperties(properties),
      measurements,
      duration
    );
  }

  /**
   * Log event
   * @param name name
   * @param properties properties
   * @param measurements measurements
   */
  logEvent(name: string, properties?: any, measurements?: any) {
    AppInsights.trackEvent(name, this.AddGlobalProperties(properties), measurements);
  }

  /**
   * Log error
   * @param error error
   * @param properties properties
   * @param measurements measurment
   */
  logError(
    error: Error,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number }
  ) {
    AppInsights.trackException(error, null, this.AddGlobalProperties(properties), measurements);
  }

  /**
   * Log user autentication
   * @param userId User ID
   */
  setAuthenticatedUserId(userId: string): void {
    AppInsights.setAuthenticatedUserContext(userId);
  }

  /**
   * Log global properties
   * @param properties properties
   */
  private AddGlobalProperties(properties?: { [key: string]: string }): { [key: string]: string } {
    if (!properties) {
      properties = {
        environment: environment.appInsightsConfig.environment,
        version: this.cID,
      };
    }

    return properties;
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      this.logError(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.console.log(`${operation} failed: ${error.message}`);

      switch (operation) {
        case 'getIdentify':
          error.actionNam = operation;
          this._store.dispatch(new PageErrorPage(error));
          break;
      }
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  createLink(csslink: string) {
    const link: HTMLLinkElement = this.doc.createElement('link');
    link.type = 'text/css';
    link.rel = 'stylesheet';
    link.href = csslink;
    this.doc.head.appendChild(link);
  }

  createScript(scriptLink: string) {
    // <script async src="https://www.googletagmanager.com/gtag/js?id=G-M7L5S3NF9V"></script>
    const link: HTMLScriptElement = this.doc.createElement('script');
    link.async = true;
    link.src = scriptLink;
    this.doc.head.appendChild(link);
  }
}
